import _ from 'lodash';
import moment from 'moment';

import env from '@/config/env';
import { bindServices } from '@/services/api';
import HttpService from '@/services/http';
import { CoreEnums } from '@/utils/domain/constants';
import { getEnumNamespace } from '@/utils/infrastructure';
import { request } from '@/utils/infrastructure/communication';
import { fileContentTypeClassification } from '@/utils/infrastructure/file-system';

/**
 * Document Manager services. Reuses generic helpers from
 * `@/services/api` (getAll/create/updateById/remove/apiRoute) bound to the
 * `documentManager` namespace for request tracking, plus temporary Azure Blob
 * upload (same pattern as Media / FormBuilder).
 */
export const api = bindServices('documentManager');

/** Namespace used by bindServices / RequestTracker / useRequestTracker. */
export const DOCUMENT_MANAGER_NAMESPACE = 'documentManager';

/**
 * Document fields used for free-text search: file name only.
 * Folders use `name`; virtual groups use the item name
 * (processName, entityName, etc.) in fetchDocumentGroups.
 */
export const DOCUMENT_SEARCH_FIELDS = ['fileName'];

/** OData filter for child folders of `parentFolderId` (null = root). */
export const buildFolderFilter = (parentFolderId) =>
  parentFolderId ? `parentFolderId eq ${parentFolderId}` : 'parentFolderId eq null';

/**
 * Grouping by "virtual folders": id/name fields returned by
 * `GET /document/groups/paged` for the requested `groupBy`.
 */
export const GROUP_FIELDS = {
  process: { id: 'processId', name: 'processName' },
  processInstance: { id: 'processInstanceId', name: 'processInstanceName' },
  form: { id: 'formId', name: 'formName' },
  entity: { id: 'entityId', name: 'entityName' },
  list: { id: 'listId', name: 'listName' },
  chatThread: { id: 'chatThreadId', name: 'chatThreadTitle' },
};

/**
 * Drill-down levels per view mode. `folder` uses the real folder hierarchy;
 * the others navigate one virtual group level and then list documents in that
 * group (e.g. Process → documents for the process).
 * Use `processInstance` mode to browse by request.
 */
export const GROUP_LEVELS = {
  folder: [],
  process: ['process'],
  processInstance: ['processInstance'],
  form: ['form'],
  entity: ['entity'],
  list: ['list'],
  chatThread: ['chatThread'],
};

/**
 * Active library: the backend does not filter `status` alone; Active must be
 * requested explicitly (same enum syntax as `/document/paged`).
 */
export const ACTIVE_DOCUMENT_STATUS_CLAUSE =
  "status eq Automy.Core.Enums.DocumentManager.DocumentStatus'Active'";

/** OData clause for group membership (null id = "unassigned"). */
export const groupRefClause = (key, id) => {
  const field = GROUP_FIELDS[key]?.id;
  if (!field) return null;
  return id == null ? `${field} eq null` : `${field} eq ${id}`;
};

const enumClause = (field, enumObj, value, additionalNamespace = null) => {
  if (_.isNil(value) || value === '') return null;
  const enumKey = Object.keys(enumObj).find((k) => enumObj[k] === value);
  if (!enumKey) return null;
  return `${field} eq ${getEnumNamespace(enumObj, additionalNamespace)}'${enumKey}'`;
};

const dateRangeClause = (range, field = 'created') => {
  if (!Array.isArray(range) || range.length < 2 || !range[0] || !range[1]) return null;
  const start = moment.isMoment(range[0]) ? range[0] : moment(range[0]);
  const end = moment.isMoment(range[1]) ? range[1] : moment(range[1]);
  return [
    `${field} gt ${start.format(env.DATE_RANGE_START_FORMAT)}`,
    `${field} lt ${end.clone().endOf('day').format(env.DATE_RANGE_END_FORMAT)}`,
  ].join(' and ');
};

const eqOrInClause = (field, value) => {
  const ids = (Array.isArray(value) ? value : [value]).filter((v) => !_.isNil(v) && v !== '');
  if (ids.length === 0) return null;
  if (ids.length === 1) return `${field} eq ${ids[0]}`;
  return `(${ids.map((id) => `${field} eq ${id}`).join(' or ')})`;
};

/**
 * Combined OData filter for the document list.
 *
 * @param {Object} opts
 * @param {string|null} opts.folderId Current folder (null = root).
 * @param {boolean} [opts.folderScope=true] When false, omits the folder
 *   clause (grouped views span documents across the whole drive).
 * @param {Array<{key,id}>} [opts.groups] Virtual groups in the current drill-down
 *   (process / request / form).
 * @param {number} [opts.fileTypeClassification] AttachmentFileTypeClassification.
 * @param {number} [opts.source] DocumentSource (document origin).
 * @param {string|string[]} [opts.processId] Source process(es).
 * @param {string} [opts.creator] Owner userId; matched with
 *   contains(creator, id) because creator may be JSON with resourceIdentifier.
 * @param {Array} [opts.dateRange] Moment range [start, end] on `created`.
 */
export const buildDocumentsFilter = ({
  folderId,
  folderScope = true,
  groups = [],
  fileTypeClassification,
  source,
  processId,
  creator,
  dateRange,
} = {}) => {
  const clauses = [
    ACTIVE_DOCUMENT_STATUS_CLAUSE,
    folderScope ? (folderId ? `folderId eq ${folderId}` : 'folderId eq null') : null,
    ...groups.map((g) => groupRefClause(g.key, g.id)),
    enumClause(
      'fileTypeClassification',
      CoreEnums.AttachmentFileTypeClassification,
      fileTypeClassification,
    ),
    enumClause('source', CoreEnums.DocumentSource, source, 'DocumentManager'),
    eqOrInClause('processId', processId),
    creator ? `contains(creator,'${String(creator).replace(/'/g, "''")}')` : null,
    dateRangeClause(dateRange),
  ].filter(Boolean);

  return clauses.join(' and ');
};

/** Compat: simple folder filter (used by views without extra filters). */
export const buildDocumentFilter = (folderId) => buildDocumentsFilter({ folderId });

const normalizeGroupItems = (items, level, field) =>
  items.map((it) => ({
    key: level,
    id: it[field.id] ?? null,
    name: it[field.name] || null,
    count: it.count ?? it.Count ?? null,
    // Aggregated bytes from /document/groups/paged (all groupBy levels).
    size: it.size ?? it.Size ?? null,
  }));

/**
 * Paginated virtual group list via the dedicated
 * `GET /document/groups/paged` endpoint (GROUP BY + COUNT in SQL).
 * Do not use `$apply` on `/document/paged`: the backend does not support it.
 *
 * `groupBy` is a custom query param (not OData); therefore `request` is
 * called with explicit params instead of `api.getAll`/`toQuery`, which
 * would drop `groupBy`.
 *
 * @param {Object} opts
 * @param {string} opts.level process|processInstance|form|entity|list|chatThread
 * @param {Array<{key,id}>} [opts.parentGroups] Previous drill-down.
 * @param {Object} [opts.filters] Advanced list filters.
 * @param {string} [opts.search] Search on the group name.
 * @param {number} [opts.page]
 * @param {number} [opts.limit] Max 500; backend default = 5 if omitted.
 * @returns {Promise<{items:Array, totalCount:number}>}
 */
export async function fetchDocumentGroups({
  level,
  parentGroups = [],
  filters = {},
  search,
  page = 1,
  limit = 25,
  readPriority = false,
}) {
  const field = GROUP_FIELDS[level];
  if (!field) {
    return { items: [], totalCount: 0 };
  }

  const baseFilter = buildDocumentsFilter({ folderScope: false, groups: parentGroups, ...filters });
  const searchClause = search
    ? `contains(${field.name},'${String(search).replace(/'/g, "''")}')`
    : null;
  const filter = [baseFilter, searchClause].filter(Boolean).join(' and ');

  // Via HttpService + namespace so rapid navigation aborts the previous
  // groups request. `groupBy` and OData keys must use passthroughParams
  // because toQuery drops non-standard query fields.
  const res = await HttpService.getInstance().request('/document/groups/paged', {
    passthroughParams: {
      groupBy: level,
      ...(filter ? { $filter: filter } : {}),
      // Only group name or `count` are allowed on this endpoint.
      $orderBy: `${field.name} asc`,
      $top: limit,
      $skip: (page - 1) * limit,
    },
    namespace: DOCUMENT_MANAGER_NAMESPACE,
    ...(readPriority && { readPriority: true }),
  });

  const items = res?.items ?? res?.value ?? [];
  return {
    items: normalizeGroupItems(items, level, field),
    totalCount: res?.totalCount ?? items.length,
  };
}

/**
 * Uploads a file to temporary storage (Azure Blob via SAS) and returns the
 * `attachment` (JsonAttachmentItem) ready for `POST /document`. Preferred
 * UX flow: the file goes directly to Azure and create only sends
 * `documentTemporaryUrl` (avoids huge payloads).
 *
 * @param {Object} file AntD Upload list item (with `originFileObj`).
 * @returns {Promise<Object>} attachment { fileName, contentType, size, fileTypeClassification, documentTemporaryUrl }
 */
export async function uploadTemporaryFile(file) {
  const raw = file?.originFileObj || file;
  const fileName = file?.name || raw?.name;
  const contentType = raw?.type || 'application/octet-stream';
  const size = raw?.size || 0;
  const fileTypeClassification = fileContentTypeClassification[contentType] ?? 0;

  const base = { fileName, contentType, size, fileTypeClassification };

  // SAS + direct PUT to the temporary blob (same pattern as Media / FormBuilder).
  const data = await request(
    `/${env.AZURE_SAS_TOKEN_URI}?fileName=${encodeURIComponent(fileName)}`,
  );
  await request.put(data.sasUri, {
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-blob-content-type': contentType,
      // Azure rejects non-ASCII in headers; keep filename= ASCII-only.
      'x-ms-blob-content-disposition': `attachment; filename="${
        String(fileName || 'file')
          .replace(/[^\x20-\x7E]/g, '_')
          .replace(/"/g, '') || 'file'
      }"; filename*=UTF-8''${encodeURIComponent(fileName || 'file')}`,
      'Content-Type': contentType,
      'Content-length': size,
    },
    body: raw,
  });

  return { ...base, documentTemporaryUrl: data.sasUri };
}

/** Max selection items for bulk export (`MaxDocumentBulkExportItemCount`). */
export const MAX_DOCUMENT_BULK_EXPORT_ITEM_COUNT = 2000;

/** Max total source bytes per bulk export (`MaxDocumentBulkExportTotalBytes` = 5 GB). */
export const MAX_DOCUMENT_BULK_EXPORT_TOTAL_BYTES = 5368709120;

/** Max items for bulk move/delete (`POST /document/items/*`). */
export const MAX_DOCUMENT_BULK_ITEMS_COUNT = 500;

const dedupeTypedItems = (items = []) => {
  const seen = new Set();
  const next = [];
  items.forEach((it) => {
    if (!it?.id || (it.type !== 'document' && it.type !== 'folder')) return;
    const key = `${it.type}:${it.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    next.push({ id: it.id, type: it.type });
  });
  return next;
};

/**
 * Starts a Document Manager bulk export (ZIP + documents.xlsx).
 * Preferred payload: typed `items` (documents and/or folders).
 * Returns 202 Accepted with DocumentBulkExportTaskResponse.
 *
 * @param {{id:string, type:'document'|'folder'}[]} items
 */
export async function exportDocuments(items) {
  return api.apiRoute({
    route: 'document/export',
    method: 'post',
    data: { items: dedupeTypedItems(items) },
  });
}

/**
 * Bulk move mixed selection to a destination folder (null = library root).
 * @param {{items: Array<{id,type}>, destinationFolderId: string|null}} payload
 */
export async function moveDocumentItems({ items, destinationFolderId = null }) {
  return api.apiRoute({
    route: 'document/items/move',
    method: 'post',
    data: {
      items: dedupeTypedItems(items),
      destinationFolderId: destinationFolderId ?? null,
    },
  });
}

/**
 * Bulk delete mixed selection (folders cascade soft-delete).
 * @param {{items: Array<{id,type}>}} payload
 */
export async function deleteDocumentItems({ items }) {
  return api.apiRoute({
    route: 'document/items/delete',
    method: 'post',
    data: { items: dedupeTypedItems(items) },
  });
}
